import { useEffect, useState, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Paperclip, Send, UserCog, GitBranch, CheckCircle2, MessageSquare, Tag, Plus, Trash2, Pencil, X, Eye, EyeOff, AtSign } from "lucide-react";
import AttachmentThumb from "@/components/AttachmentThumb";
import DriveImport from "@/components/DriveImport";
import MentionTextarea from "@/components/MentionTextarea";
import { timeAgo, PRIORITIES } from "@/lib/helpers";
import { useAuth } from "@/context/AuthContext";

const UNASSIGNED = "__unassigned__";
const NONE = "__none__";

const ICONS = {
  created: Plus,
  status_changed: CheckCircle2,
  category_changed: Tag,
  reassigned: GitBranch,
  assigned: UserCog,
  comment: MessageSquare,
  mention: AtSign,
  attachment_added: Paperclip,
  updated: CheckCircle2,
};

export default function TaskDrawer({ taskId, onClose, statuses, categories, users, locations = [], onChange }) {
  const { user } = useAuth();
  const [task, setTask] = useState(null);
  const [comment, setComment] = useState("");
  const [editingUpdateId, setEditingUpdateId] = useState(null);
  const [editUpdateVal, setEditUpdateVal] = useState("");
  const [reassignOpen, setReassignOpen] = useState(false);
  const [newAssignee, setNewAssignee] = useState(UNASSIGNED);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [editTitle, setEditTitle] = useState(false);
  const [titleVal, setTitleVal] = useState("");
  const [editDesc, setEditDesc] = useState(false);
  const [descVal, setDescVal] = useState("");
  const fileRef = useRef();

  const canManage = user?.role === "admin" || user?.role === "manager";

  const load = () => api.get(`/tasks/${taskId}`).then((r) => setTask(r.data));
  useEffect(() => { load(); }, [taskId]);

  const patch = async (body) => {
    try {
      const { data } = await api.patch(`/tasks/${taskId}`, body);
      setTask(data);
      onChange?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveTitle = async () => {
    const v = titleVal.trim();
    if (!v) return toast.error("Title is required");
    await patch({ title: v });
    setEditTitle(false);
  };

  const saveDesc = async () => {
    await patch({ description: descVal });
    setEditDesc(false);
  };

  const mentionIds = (text) => users.filter((u) => {
    if (!u.name) return false;
    const esc = u.name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    return new RegExp(`@${esc}(?!\\w)`).test(text);
  }).map((u) => u.id);

  const renderMentions = (text) => {
    if (!text) return text;
    const names = users.map((u) => u.name).filter(Boolean).sort((a, b) => b.length - a.length);
    if (!names.length) return text;
    const esc = names.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
    const re = new RegExp(`@(${esc.join("|")})`, "g");
    const parts = [];
    let last = 0, m;
    while ((m = re.exec(text)) !== null) {
      if (m.index > last) parts.push(text.slice(last, m.index));
      parts.push(<span key={m.index} className="font-semibold text-[#e84e34]">{m[0]}</span>);
      last = m.index + m[0].length;
    }
    if (last < text.length) parts.push(text.slice(last));
    return parts;
  };

  const addComment = async () => {
    if (!comment.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/tasks/${taskId}/updates`, { message: comment, mentions: mentionIds(comment) });
      setTask(data); setComment(""); onChange?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const saveUpdateEdit = async (updateId) => {
    if (!editUpdateVal.trim()) return;
    try {
      const { data } = await api.patch(`/tasks/${taskId}/updates/${updateId}`, { message: editUpdateVal, mentions: mentionIds(editUpdateVal) });
      setTask(data); setEditingUpdateId(null); onChange?.();
      toast.success("Update edited");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const reassign = async () => {
    const isHandoff = !!task.assignee_id;
    if (isHandoff && !reason.trim()) return toast.error("A handoff reason is required");
    setBusy(true);
    try {
      const { data } = await api.post(`/tasks/${taskId}/reassign`, { assignee_id: newAssignee === UNASSIGNED ? null : newAssignee, reason: isHandoff ? reason : "" });
      setTask(data); setReassignOpen(false); setReason(""); onChange?.();
      toast.success(isHandoff ? "Task reassigned" : "Task assigned");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post(`/tasks/${taskId}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setTask(data); onChange?.();
      toast.success("Attachment added");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const remove = async () => {
    if (!window.confirm("Delete this task?")) return;
    try { await api.delete(`/tasks/${taskId}`); toast.success("Task deleted"); onChange?.(); onClose(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const isWatching = (task?.watchers || []).includes(user?.id);
  const toggleWatch = async () => {
    try {
      const { data } = await api[isWatching ? "delete" : "post"](`/tasks/${taskId}/watch`);
      setTask(data);
      toast.success(isWatching ? "Stopped watching" : "You're now watching this task");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const activity = [...(task?.activity || [])].reverse();

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto rounded-none border-l border-zinc-200 p-0 sm:max-w-2xl">
        <SheetTitle className="sr-only">Task details</SheetTitle>
        {!task ? (
          <div className="flex h-full items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#e84e34]" /></div>
        ) : (
          <div className="flex min-h-full flex-col">
            <div className="flex items-start justify-between border-b border-zinc-200 p-6">
              <div className="pr-6">
                <p className="text-xs font-medium uppercase tracking-wider text-[#e84e34]">Task</p>
                {editTitle ? (
                  <div className="mt-1 flex items-center gap-2">
                    <input autoFocus value={titleVal} onChange={(e) => setTitleVal(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") saveTitle(); if (e.key === "Escape") setEditTitle(false); }} data-testid="edit-task-title-input" className="w-full border border-zinc-200 px-2 py-1 font-heading text-xl font-bold tracking-tight text-zinc-900 outline-none focus:border-[#e84e34]" />
                    <button onClick={saveTitle} data-testid="save-task-title" className="flex-shrink-0 bg-zinc-900 px-2 py-1.5 text-xs font-medium text-white hover:bg-zinc-800">Save</button>
                    <button onClick={() => setEditTitle(false)} className="flex-shrink-0 text-zinc-400 hover:text-[#e84e34]"><X className="h-4 w-4" /></button>
                  </div>
                ) : (
                  <h2 className="mt-1 flex items-center gap-2 font-heading text-xl font-bold tracking-tight text-zinc-900" data-testid="drawer-task-title">
                    {task.title}
                    {canManage && <button onClick={() => { setTitleVal(task.title); setEditTitle(true); }} data-testid="edit-task-title-button" title="Edit title" className="text-zinc-300 hover:text-[#e84e34]"><Pencil className="h-3.5 w-3.5" /></button>}
                  </h2>
                )}
              </div>
              {canManage && (
                <button onClick={remove} data-testid="delete-task-button" className="mr-8 p-1 text-zinc-400 hover:text-[#e84e34]"><Trash2 className="h-4 w-4" /></button>
              )}
            </div>
            <div className="flex items-center gap-2 border-b border-zinc-200 px-6 py-2.5">
              <button onClick={toggleWatch} data-testid="watch-toggle" className={`flex items-center gap-1.5 border px-3 py-1.5 text-xs font-medium transition-colors ${isWatching ? "border-[#e84e34] bg-[#fdece8] text-[#e84e34]" : "border-zinc-200 text-zinc-600 hover:border-[#e84e34] hover:text-[#e84e34]"}`}>
                {isWatching ? <><Eye className="h-3.5 w-3.5" /> Watching</> : <><EyeOff className="h-3.5 w-3.5" /> Watch</>}
              </button>
              {(task.watchers || []).length > 0 && <span className="text-xs text-zinc-400" data-testid="watcher-count">{task.watchers.length} watching</span>}
            </div>

            <div className="grid flex-1 grid-cols-1 lg:grid-cols-2">
              {/* Left: data */}
              <div className="space-y-5 border-r border-zinc-200 p-6">
                <div>
                  <div className="mb-1 flex items-center justify-between">
                    <label className="block text-xs font-medium uppercase tracking-wider text-zinc-500">Description</label>
                    {canManage && !editDesc && <button onClick={() => { setDescVal(task.description || ""); setEditDesc(true); }} data-testid="edit-task-desc-button" className="flex items-center gap-1 text-xs font-medium text-[#e84e34] hover:underline"><Pencil className="h-3 w-3" /> Edit</button>}
                  </div>
                  {editDesc ? (
                    <div className="space-y-2">
                      <textarea autoFocus value={descVal} onChange={(e) => setDescVal(e.target.value)} rows={4} data-testid="edit-task-desc-input" className="w-full resize-none border border-zinc-200 px-2 py-1.5 text-sm outline-none focus:border-[#e84e34]" />
                      <div className="flex gap-2">
                        <button onClick={saveDesc} data-testid="save-task-desc" className="bg-zinc-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-zinc-800">Save</button>
                        <button onClick={() => setEditDesc(false)} className="px-2 py-1.5 text-xs text-zinc-500 hover:text-[#e84e34]">Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <p className="whitespace-pre-wrap text-sm text-zinc-700">{task.description || "—"}</p>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-zinc-500">Status</label>
                    <Select value={task.status_id || NONE} onValueChange={(v) => patch({ status_id: v === NONE ? null : v })}>
                      <SelectTrigger data-testid="drawer-status-select" className="rounded-none"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NONE}>None</SelectItem>
                        {statuses.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-zinc-500">Category</label>
                    <Select value={task.category_id || NONE} onValueChange={(v) => patch({ category_id: v === NONE ? null : v })}>
                      <SelectTrigger data-testid="drawer-category-select" className="rounded-none"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NONE}>None</SelectItem>
                        {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-zinc-500">Priority</label>
                    <Select value={task.priority} onValueChange={(v) => patch({ priority: v })}>
                      <SelectTrigger data-testid="drawer-priority-select" className="rounded-none"><SelectValue /></SelectTrigger>
                      <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-zinc-500">Due date</label>
                    <input type="date" value={task.due_date || ""} onChange={(e) => patch({ due_date: e.target.value })} className="w-full border border-zinc-200 px-2 py-1.5 text-sm outline-none focus:border-[#e84e34]" />
                  </div>
                  <div className="col-span-2">
                    <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-zinc-500">Location</label>
                    <Select value={task.location_id || NONE} onValueChange={(v) => patch({ location_id: v === NONE ? null : v })}>
                      <SelectTrigger data-testid="drawer-location-select" className="rounded-none"><SelectValue placeholder="General" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NONE}>General (no location)</SelectItem>
                        {locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-medium uppercase tracking-wider text-zinc-500">Assignee</label>
                    <button onClick={() => { setReassignOpen(!reassignOpen); setNewAssignee(task.assignee_id || UNASSIGNED); }} data-testid="reassign-toggle" className="flex items-center gap-1 text-xs font-medium text-[#e84e34] hover:underline">
                      <UserCog className="h-3 w-3" /> {task.assignee_id ? "Reassign" : "Assign"}
                    </button>
                  </div>
                  <p className="mt-1 text-sm font-medium text-zinc-900">{task.assignee_name || "Unassigned"}</p>
                  {reassignOpen && (
                    <div className="mt-2 space-y-2 border border-zinc-200 bg-zinc-50 p-3">
                      <Select value={newAssignee} onValueChange={setNewAssignee}>
                        <SelectTrigger data-testid="reassign-select" className="rounded-none bg-white"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value={UNASSIGNED}>Unassigned</SelectItem>
                          {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      {task.assignee_id && <textarea data-testid="reassign-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for handoff (required)" rows={2} className="w-full border border-zinc-200 px-2 py-1.5 text-sm outline-none focus:border-[#e84e34]" />}
                      <button onClick={reassign} disabled={busy} data-testid="reassign-confirm" className="flex items-center gap-2 bg-[#e84e34] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#c73e26] disabled:opacity-60">
                        {busy && <Loader2 className="h-3 w-3 animate-spin" />} {task.assignee_id ? "Confirm handoff" : "Assign"}
                      </button>
                    </div>
                  )}
                </div>

                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <label className="text-xs font-medium uppercase tracking-wider text-zinc-500">Attachments</label>
                    <div className="flex items-center gap-3">
                      <DriveImport targetType="task" targetId={taskId} onImported={(parent) => { if (parent) setTask(parent); onChange?.(); }} />
                      <button onClick={() => fileRef.current?.click()} data-testid="upload-attachment-button" className="flex items-center gap-1 text-xs font-medium text-[#e84e34] hover:underline"><Paperclip className="h-3 w-3" /> Add</button>
                    </div>
                    <input ref={fileRef} type="file" className="hidden" onChange={upload} />
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {(task.attachments || []).map((f) => <AttachmentThumb key={f.id} file={f} />)}
                    {(task.attachments || []).length === 0 && <p className="text-xs text-zinc-400">No attachments</p>}
                  </div>
                </div>
              </div>

              {/* Right: activity log */}
              <div className="flex flex-col bg-zinc-50/50">
                <div className="border-b border-zinc-200 p-4">
                  <h3 className="font-heading text-sm font-semibold uppercase tracking-wider text-zinc-700">Activity Log</h3>
                </div>
                <div className="flex-1 space-y-4 overflow-y-auto p-4" data-testid="activity-log">
                  {activity.map((a) => {
                    const Icon = ICONS[a.type] || CheckCircle2;
                    const isComment = a.type === "comment";
                    return (
                      <div key={a.id} className="flex gap-3">
                        <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center border border-zinc-200 bg-white text-zinc-500">
                          <Icon className="h-3 w-3" />
                        </div>
                        <div className="min-w-0 flex-1">
                          {isComment ? (
                            <div className="border border-zinc-200 bg-white p-2.5">
                              <div className="flex items-center justify-between">
                                <p className="text-xs font-medium text-zinc-900">{a.user_name}</p>
                                {(a.user_id === user?.id || canManage) && editingUpdateId !== a.id && (
                                  <button onClick={() => { setEditingUpdateId(a.id); setEditUpdateVal(a.message); }} data-testid={`edit-update-${a.id}`} className="text-zinc-300 hover:text-[#e84e34]"><Pencil className="h-3 w-3" /></button>
                                )}
                              </div>
                              {editingUpdateId === a.id ? (
                                <div className="mt-1.5 space-y-2">
                                  <MentionTextarea value={editUpdateVal} onChange={setEditUpdateVal} users={users} testid={`edit-update-input-${a.id}`} autoFocus />
                                  <div className="flex gap-2">
                                    <button onClick={() => saveUpdateEdit(a.id)} data-testid={`save-update-${a.id}`} className="bg-zinc-900 px-3 py-1 text-xs font-medium text-white hover:bg-zinc-800">Save</button>
                                    <button onClick={() => setEditingUpdateId(null)} className="px-2 py-1 text-xs text-zinc-500 hover:text-[#e84e34]">Cancel</button>
                                  </div>
                                </div>
                              ) : (
                                <p className="mt-0.5 whitespace-pre-wrap text-sm text-zinc-700">
                                  {renderMentions(a.message)}
                                  {a.edited_at && <span className="ml-1 text-[10px] italic text-zinc-400">(edited)</span>}
                                </p>
                              )}
                            </div>
                          ) : (
                            <p className="text-sm text-zinc-700"><span className="font-medium text-zinc-900">{a.user_name}</span> {a.message}</p>
                          )}
                          {a.reason && (
                            <div className="mt-1 border-l-2 border-[#e84e34] bg-[#fdece8] px-2 py-1">
                              <p className="text-[10px] font-semibold uppercase tracking-wider text-[#e84e34]">Handoff reason</p>
                              <p className="text-xs text-zinc-700">{a.reason}</p>
                            </div>
                          )}
                          <p className="mt-0.5 text-[11px] text-zinc-400">{timeAgo(a.created_at)}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="border-t border-zinc-200 bg-white p-3">
                  <div className="flex items-end gap-2">
                    <MentionTextarea value={comment} onChange={setComment} users={users} placeholder="Post an update… use @ to mention" testid="comment-input" />
                    <button onClick={addComment} disabled={busy} data-testid="comment-submit" className="flex h-9 w-9 items-center justify-center bg-[#e84e34] text-white hover:bg-[#c73e26] disabled:opacity-60">
                      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
